// grid/buildValidationColumns.ts
import type { ColDef, ValueGetterParams } from "ag-grid-enterprise";
import type { IRoomSummary } from "@backend/src/schemas";
import type { IValidationColumnMeta } from "./validationColumnMetas";

const getByPath = (obj: unknown, path: string[]): unknown => {
  if (obj == null) return undefined;

  let cur: unknown = obj;

  for (const seg of path) {
    if (cur == null) return undefined;
    if (typeof cur !== "object") return undefined;

    const rec = cur as Record<string, unknown>;
    cur = rec[seg];
  }

  return cur;
};

export interface BuildValidationColumnsOptions {
  percentCellRenderer?: string;

  /** Name of registered AG Grid component, e.g. "ValidationStatusBadgeCellRenderer" */
  statusCellRenderer?: string;

  formatPercent?: (v: number | undefined) => string;
  emptyText?: string;
}

const defaultFormatPercent = (v?: number) => (typeof v === "number" ? `${Math.round(v)} %` : "—");

export const buildValidationColumns = (
  metas: IValidationColumnMeta[],
  opts: BuildValidationColumnsOptions = {}
): ColDef<IRoomSummary>[] => {
  const {
    percentCellRenderer,
    statusCellRenderer = "ValidationStatusBadgeCellRenderer",
    formatPercent = defaultFormatPercent,
    emptyText = "—",
  } = opts;

  return metas.map<ColDef<IRoomSummary>>((meta) => {
    const isPercentColumn = meta.id.includes("matchRatePct");
    const isStatusColumn = typeof meta.valueToColor === "function";

    const base: ColDef<IRoomSummary> = {
      colId: meta.id,
      headerName: meta.header,
      headerTooltip: meta.tooltip,
      width: isPercentColumn ? 130 : isStatusColumn ? 170 : 190,
      sortable: true,
      filter: true,
    };

    // 1) Percent column (matchRatePct)
    if (isPercentColumn) {
      return {
        ...base,
        filter: "agNumberColumnFilter",
        valueGetter: (p: ValueGetterParams<IRoomSummary>) => {
          const raw = getByPath(p.data, meta.valuePath);
          return typeof raw === "number" ? raw : undefined;
        },
        valueFormatter: (p) => formatPercent(p.value as number | undefined),
        tooltipValueGetter: (p) => {
          const v = p.value as number | undefined;
          return typeof v === "number" ? `${v.toFixed(0)} %` : emptyText;
        },
        ...(percentCellRenderer
          ? {
              cellRenderer: percentCellRenderer,
              cellRendererParams: {
                getValue: (row: IRoomSummary) =>
                  getByPath(row, meta.valuePath) as number | undefined,
              },
            }
          : {}),
      };
    }

    // 2) Status column (Badge + color)
    if (isStatusColumn) {
      return {
        ...base,
        valueGetter: (p: ValueGetterParams<IRoomSummary>) => getByPath(p.data, meta.valuePath),
        cellRenderer: statusCellRenderer,
        cellRendererParams: { meta },
        tooltipValueGetter: () => meta.tooltip ?? "",
      };
    }

    // 3) Plain columns (text/date/name)
    return {
      ...base,
      valueGetter: (p: ValueGetterParams<IRoomSummary>) => {
        const raw = getByPath(p.data, meta.valuePath);
        if (meta.valueFormatter) return meta.valueFormatter(raw);
        return raw ?? emptyText;
      },
    };
  });
};
